import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DocumentPreviewCarousel from "@/components/DocumentPreviewCarousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, X, Star, ArrowRight } from "lucide-react";
import { ADD_ONS, addOnPrice } from "@/lib/addons";
import { SEO } from "@/components/SEO";
import { SITE_URL, OG_IMAGE } from "@/lib/seo";

const PACKAGES = [
  { id: "basic",    name: "Basic",    price: 39, express: 89,  popular: false },
  { id: "complete", name: "Complete", price: 69, express: 119, popular: true  },
  { id: "premium",  name: "Premium",  price: 99, express: 149, popular: false },
];

const PACKAGE_FEATURES_LIST = {
  basic:    ["Visa Application Form", "Flight Reservation with valid PNR", "Hotel Booking Confirmation"],
  complete: ["Visa Application Form", "Flight Reservation with valid PNR", "Hotel Booking Confirmation", "Car Rental Reservation", "Cover Letter"],
  premium:  ["Visa Application Form", "Flight Reservation with valid PNR", "Hotel Booking Confirmation", "Car Rental Reservation", "Cover Letter", "Travel Itinerary & Day-by-Day Plan", "Travel Insurance (€30,000)", "24/7 WhatsApp Support"],
};

const COMPARE_ROWS = [
  { label: "Visa Application Form",                  basic: true,  complete: true,  premium: true,  type: "bool" },
  { label: "Flight Reservation (valid PNR)",         basic: true,  complete: true,  premium: true,  type: "bool" },
  { label: "Hotel Booking Confirmation",             basic: true,  complete: true,  premium: true,  type: "bool" },
  { label: "Car Rental Reservation",                 basic: false, complete: true,  premium: true,  type: "bool" },
  { label: "Cover Letter",                           basic: false, complete: true,  premium: true,  type: "bool" },
  { label: "Travel Itinerary & Day-by-Day Plan",     basic: false, complete: false, premium: true,  type: "bool" },
  { label: "Travel Insurance (€30,000)",             basic: false, complete: false, premium: true,  type: "bool" },
  { label: "24/7 WhatsApp Support",                  basic: false, complete: false, premium: true,  type: "bool" },
  { label: "Delivery time",                          basic: "24h", complete: "24h", premium: "24h", type: "text" },
  { label: "Express delivery",                       basic: "+$50",complete: "+$50",premium: "+$50",type: "money" },
  { label: "Price per applicant",                    basic: "$39", complete: "$69", premium: "$99", type: "price" },
];

const ADDON_ROWS = [
  { label: "No Objection Certificate (NOC)" },
  { label: "Sponsor Letter" },
  { label: "Invitation Letter" },
];

const VS_ROWS = [
  ["Price per applicant",        "$150 – $300+",                          "$39 – $99",                          "bad", "good"],
  ["Delivery time",              "2–5 business days",                     "24h or 3h Express",                  "bad", "good"],
  ["Office visit required",      "Yes",                                   "No — 100% online",                   "bad", "good"],
  ["Available 24/7",             "No",                                    "Yes",                                "bad", "good"],
  ["Transparent pricing",        "Hidden fees common",                    "Always clear",                       "bad", "good"],
  ["Flight reservation",         "Generic PDF",                           "Real GDS PNR (verifiable)",          "bad", "good"],
  ["Document quality",           "Varies",                                "Consulate-standard",                 "bad", "good"],
  ["Family applications",        "Separate files, extra cost",            "One order, organized",               "bad", "good"],
  ["Free revisions",             "Extra charge",                          "Free within 48h",                    "bad", "good"],
  ["Payment security",           "Cash or bank transfer",                 "Stripe 3D Secure",                   "bad", "good"],
  ["Chargeback protection",      "None",                                  "Full 3DS liability shift",           "bad", "good"],
  ["Refund guarantee",           "Rarely",                                "Yes — if not delivered on time",     "bad", "good"],
];

const PRICING_FAQS = [
  { q: "Is this a one-time payment?", a: "Yes. You pay once per order. There are no subscriptions, hidden fees, or recurring charges." },
  { q: "How much does it cost per applicant?", a: "Basic is $39, Complete is $69, and Premium is $99 per applicant. Express delivery adds $50 to any package. Optional add-ons (NOC, Sponsor Letter, Invitation Letter) are $19 each." },
  { q: "What's included in each package?", a: "Basic includes Visa Application Form, Flight Reservation (with verifiable PNR), and Hotel Booking Confirmation. Complete adds Car Rental Reservation and Cover Letter. Premium adds Travel Itinerary, Travel Insurance (€30,000), and 24/7 WhatsApp Support." },
  { q: "What are the add-ons and who needs them?", a: "We offer three optional add-ons at $19 each: No Objection Certificate for employed applicants, Sponsor Letter if someone else covers your costs, and Invitation Letter if you're staying with a host in the Schengen area." },
  { q: "What is Express delivery?", a: "Express delivery (+$50) means your documents are ready within 3 hours during business hours, instead of the standard 24 hours." },
  { q: "Do you offer refunds?", a: "Yes. If we fail to deliver within the promised timeframe, you are entitled to a full refund. We also offer free re-issuance within 48 hours if your details change." },
  { q: "Can I apply for multiple people?", a: "Yes. When placing your order, set the number of applicants. Each applicant is charged at the package price — for example, 2 applicants on Complete = 2 × $69 = $138." },
  { q: "Is payment secure?", a: "Yes. All payments are processed via Stripe with 3D Secure encryption. We never store your card details." },
];

export default function Pricing() {
  const [delivery, setDelivery] = useState("normal");
  const [selectedPkg, setSelectedPkg] = useState("complete");
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Always start at top
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);

  const priceFor = (p) => (delivery === "express" ? p.express : p.price);
  const toggleAddon = (id) => {
    setSelectedAddons((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const addOnTotal = addOnPrice(selectedAddons);
  const selectedPkgObj = PACKAGES.find((p) => p.id === selectedPkg) || PACKAGES[1];
  const grandTotal = priceFor(selectedPkgObj) + addOnTotal;

  const applyHrefFor = (pkgId) => {
    const params = new URLSearchParams({ package: pkgId, delivery });
    if (selectedAddons.length > 0) params.set("addons", selectedAddons.join(","));
    return `/apply?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]">
      <SEO
        title="Schengen Visa Document Packages — $39 to $99 | SchengenFile"
        description="Basic $39, Complete $69, Premium $99. Real flight PNR, hotel booking, €30,000 insurance and application form. Delivered in 24 hours or 3 hours express."
        jsonLd={PACKAGES.map((p) => ({
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${p.name} Schengen Visa Document Package`,
          image: [OG_IMAGE],
          brand: { "@type": "Brand", name: "SchengenFile" },
          category: "Travel Documentation Service",
          description: PACKAGE_FEATURES_LIST[p.id].join(" · "),
          url: `${SITE_URL}/pricing#${p.id}`,
          offers: {
            "@type": "Offer",
            price: p.price.toString(),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `${SITE_URL}/apply?package=${p.id}`,
          },
        }))}
      />
      <Header />

      <main>
      {/* 1. Page header */}
      <section className="relative py-16 sm:py-20 bg-[#0f1b2d] overflow-hidden" data-testid="pricing-header">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1b2d] via-[#0f1b2d] to-[#1e3a8a]/70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#60a5fa]">Pricing</div>
          <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl">Choose your Schengen visa file package</h1>
          <p className="mt-4 text-base sm:text-lg text-white/75 max-w-2xl mx-auto">One-time payment. No subscription. Delivered by certified travel agents.</p>
        </div>
      </section>

      {/* 2. Interactive application widget — reuses the landing hero card */}
      <section className="py-12 sm:py-16 bg-[#f8fafc]" data-testid="pricing-widget-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">Start instantly</div>
            <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#0f1b2d]">Pick your destination, choose your package, get your file</h2>
            <p className="mt-3 text-[#475569] max-w-xl leading-relaxed">Tell us where you&apos;re going. Our widget will show you which package is right for you and exactly what you&apos;ll receive.</p>
            <ul className="mt-6 space-y-2 text-sm text-[#475569]" data-testid="pricing-widget-bullets">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#16a34a] mt-0.5 flex-shrink-0" /> Consulate-grade documents — no generic templates</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#16a34a] mt-0.5 flex-shrink-0" /> 24h delivery — or 3h with Express (+$50)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#16a34a] mt-0.5 flex-shrink-0" /> Free re-issuance within 48 hours if your details change</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#16a34a] mt-0.5 flex-shrink-0" /> Stripe 3D Secure payments — full chargeback protection</li>
            </ul>
          </div>
          <div className="lg:col-span-5 w-full flex justify-center lg:justify-end">
            <DocumentPreviewCarousel />
          </div>
        </div>
      </section>

      {/* 3. Package comparison cards — ALWAYS horizontal */}
      <section className="py-16" data-testid="pricing-cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0f1b2d]">Everything included in your package</h2>
            <p className="mt-3 text-[#475569]">One-time payment per applicant. No hidden fees.</p>
            {/* Delivery toggle */}
            <div className="mt-6 inline-flex rounded-md border border-[#e2e8f0] bg-white p-1" data-testid="pricing-delivery-toggle">
              <button type="button" onClick={() => setDelivery("normal")} data-testid="pricing-delivery-normal"
                className={`px-3 sm:px-4 py-1.5 text-xs font-semibold rounded transition ${delivery === "normal" ? "bg-[#2563eb] text-white" : "text-[#475569]"}`}>24h Delivery</button>
              <button type="button" onClick={() => setDelivery("express")} data-testid="pricing-delivery-express"
                className={`px-3 sm:px-4 py-1.5 text-xs font-semibold rounded transition ${delivery === "express" ? "bg-[#2563eb] text-white" : "text-[#475569]"}`}>Express 3h (+$50)</button>
            </div>
          </div>

          {/* Horizontal on desktop (md+); vertical full-width stack on mobile */}
          <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-testid="pricing-cards">
            {PACKAGES.map((p) => {
              const items = PACKAGE_FEATURES_LIST[p.id];
              const isSelected = selectedPkg === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  onClick={() => setSelectedPkg(p.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedPkg(p.id); } }}
                  className={`relative rounded-xl border-2 p-5 md:p-6 bg-white flex flex-col cursor-pointer transition-all
                    ${isSelected ? "border-[#2563eb] shadow-lg ring-2 ring-[#2563eb]/15" : p.popular ? "border-[#2563eb]/50 hover:border-[#2563eb]" : "border-[#e2e8f0] hover:border-[#94a3b8]"}`}
                >
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-[#2563eb] text-white rounded whitespace-nowrap">Most popular</div>
                  )}
                  <div className="text-xs uppercase tracking-widest text-[#64748b] font-semibold">{p.name}</div>
                  <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                    <span className="font-display font-black text-4xl text-[#0f1b2d]">${priceFor(p)}</span>
                    <span className="text-xs text-[#64748b]">per applicant</span>
                  </div>
                  <div className="mt-1 text-xs text-[#475569]">{delivery === "express" ? "3h delivery" : "24h delivery"}</div>
                  <ul className="mt-4 space-y-2 flex-1" data-testid={`pricing-card-${p.id}-features`}>
                    {items.map((label) => (
                      <li key={label} className="flex items-start gap-2 text-sm text-[#0f1b2d] leading-snug">
                        <Check className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" /> <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={applyHrefFor(p.id)}
                    data-testid={`pricing-card-${p.id}-cta`}
                    onClick={(e) => e.stopPropagation()}
                    className={`mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md transition-all
                      ${p.popular ? "bg-[#2563eb] hover:bg-[#1d4ed8] text-white" : "bg-[#0f1b2d] hover:bg-[#1e2a44] text-white"}`}
                  >
                    Get {p.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Optional Add-ons */}
          <div className="mt-10" data-testid="pricing-addons-section">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748b]">Optional Add-ons</div>
            <p className="mt-1 text-xs sm:text-sm text-[#475569]">Upgrade your file with these extras. Each <span className="font-semibold text-[#0f1b2d]">+$19</span>.</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {ADD_ONS.map((a) => {
                const checked = selectedAddons.includes(a.id);
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`pricing-addon-toggle-${a.id}`}
                    onClick={() => toggleAddon(a.id)}
                    className={`relative text-left p-4 rounded-md border-2 transition-all
                      ${checked ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#94a3b8]"}`}
                  >
                    <div className={`absolute top-3 right-3 w-5 h-5 rounded-md grid place-items-center transition ${checked ? "bg-[#2563eb] text-white" : "border-2 border-[#cbd5e1] bg-white"}`}>
                      {checked && <Check className="w-3 h-3" strokeWidth={3} />}
                    </div>
                    <div className="flex items-center gap-2 pr-7">
                      <Icon className={`w-4 h-4 ${checked ? "text-[#2563eb]" : "text-[#475569]"}`} />
                      <span className="font-semibold text-[#0f1b2d] text-sm">{a.name}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-[#475569] pr-7">{a.description}</div>
                    <div className="mt-2 inline-flex items-center text-xs font-display font-bold text-[#2563eb]">+${a.price}</div>
                  </button>
                );
              })}
            </div>

            {/* Running total */}
            <div className="mt-5 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="pricing-total-row">
              <div className="text-xs sm:text-sm text-[#475569]">
                <span className="font-semibold text-[#0f1b2d]">{selectedPkgObj.name}</span>
                <span className="text-[#64748b]"> · {delivery === "express" ? "Express 3h" : "24h delivery"}</span>
                {selectedAddons.length > 0 && (
                  <span className="text-[#64748b]"> · {selectedAddons.length} add-on{selectedAddons.length === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold">Total</span>
                <span data-testid="pricing-total-amount" className="font-display font-black text-2xl sm:text-3xl text-[#0f1b2d]">${grandTotal}</span>
              </div>
            </div>
          </div>
        </div>

        {/* "None of these packages fit your needs?" — outlined CTA to /build-your-file */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center" data-testid="pricing-build-your-file-link-row">
          <p className="text-sm text-[#475569]">None of these packages fit your needs?</p>
          <Link
            to="/build-your-file"
            data-testid="pricing-to-build-your-file"
            className="mt-3 inline-flex items-center gap-2 border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#eff6ff] font-semibold px-6 py-2.5 rounded-md transition"
          >
            Build your own file <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* 4. Feature comparison table — same 4-column table at all sizes */}
      <section className="py-16 bg-[#f8fafc] border-y border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center px-3 sm:px-0">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0f1b2d]">See exactly what&apos;s included</h2>
            <p className="mt-2 text-[#475569]">Compare packages side by side</p>
          </div>

          <div className="mt-8 sm:mt-10 border border-[#e2e8f0] bg-white" data-testid="comparison-table-wrapper">
            <table className="w-full table-fixed" data-testid="comparison-table">
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold bg-[#0f1b2d] text-white text-[12px] sm:text-sm">Feature</th>
                  <th className="text-center px-1 sm:px-3 py-2 sm:py-3 font-semibold bg-[#0f1b2d] text-white text-[12px] sm:text-sm">Basic</th>
                  <th className="text-center px-1 sm:px-3 py-2 sm:py-3 font-semibold bg-[#2563eb] text-white text-[12px] sm:text-sm">Complete</th>
                  <th className="text-center px-1 sm:px-3 py-2 sm:py-3 font-semibold bg-[#0f1b2d] text-white text-[12px] sm:text-sm">Premium</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-[#0f1b2d] text-[12px] sm:text-sm leading-snug break-words">{row.label}</td>
                    <CompareCell row={row} pkg="basic" />
                    <CompareCell row={row} pkg="complete" highlight />
                    <CompareCell row={row} pkg="premium" />
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} className="px-2 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-[11px] uppercase tracking-widest font-semibold text-[#64748b] bg-[#f1f5f9] border-y border-[#e2e8f0]">Optional Add-ons</td>
                </tr>
                {ADDON_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-[#0f1b2d] text-[12px] sm:text-sm leading-snug break-words">{row.label}</td>
                    <td className="text-center px-1 sm:px-3 py-2 sm:py-3 text-[#2563eb] font-bold text-[12px] sm:text-[13px]">+$19</td>
                    <td className="text-center px-1 sm:px-3 py-2 sm:py-3 text-[#2563eb] font-bold text-[12px] sm:text-[13px] bg-[#eff6ff]">+$19</td>
                    <td className="text-center px-1 sm:px-3 py-2 sm:py-3 text-[#2563eb] font-bold text-[12px] sm:text-[13px]">+$19</td>
                  </tr>
                ))}
                {/* CTAs row — one button directly under each package column */}
                <tr className="bg-white border-t-2 border-[#e2e8f0]" data-testid="comparison-cta-row">
                  <td className="px-2 sm:px-4 py-3 sm:py-4"></td>
                  {PACKAGES.map((p) => (
                    <td key={p.id} className={`px-1 sm:px-2 py-3 sm:py-4 text-center ${p.popular ? "bg-[#eff6ff]" : ""}`}>
                      <Link
                        to={applyHrefFor(p.id)}
                        data-testid={`comparison-cta-${p.id}`}
                        className={`inline-flex items-center justify-center gap-1 text-[10px] sm:text-xs lg:text-sm font-semibold px-1.5 sm:px-3 py-1.5 sm:py-2.5 rounded-md transition w-full ${p.popular ? "bg-[#2563eb] hover:bg-[#1d4ed8] text-white" : "bg-white text-[#2563eb] border-2 border-[#2563eb] hover:bg-[#eff6ff]"}`}
                      >
                        Get {p.name}
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 5. SchengenFile vs Traditional Agency — same 3-column table at all sizes */}
      <section className="py-16" data-testid="vs-section">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center px-3 sm:px-0">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0f1b2d]">Why SchengenFile?</h2>
            <p className="mt-3 text-[#475569]">See how we compare to traditional visa agencies and document services</p>
          </div>

          <div className="mt-8 sm:mt-10 rounded-lg border border-[#e2e8f0] bg-white overflow-hidden" data-testid="vs-table-wrapper">
            <table className="w-full table-fixed" data-testid="vs-table">
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "33%" }} />
                <col style={{ width: "33%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-left px-2 sm:px-4 py-1.5 sm:py-3 font-semibold bg-[#f8fafc] text-[#475569] border-b border-[#e2e8f0] text-[11px] sm:text-sm">Feature</th>
                  <th className="text-left px-2 sm:px-4 py-1.5 sm:py-3 font-semibold bg-[#475569] text-white border-b border-[#e2e8f0] text-[11px] sm:text-sm">Traditional Agency</th>
                  <th className="text-left px-2 sm:px-4 py-1.5 sm:py-3 font-semibold bg-[#2563eb] text-white border-b border-[#e2e8f0] text-[11px] sm:text-sm">SchengenFile</th>
                </tr>
              </thead>
              <tbody>
                {VS_ROWS.map(([label, trad, sf], i) => (
                  <tr key={label} className={i % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
                    <td className="px-2 sm:px-4 py-1.5 sm:py-3 text-[#0f1b2d] font-semibold text-left text-[11px] sm:text-sm leading-snug break-words">{label}</td>
                    <td className="px-2 sm:px-4 py-1.5 sm:py-3 text-left align-top">
                      <span className="inline-flex items-start gap-1.5 sm:gap-2 text-[#475569] text-[11px] sm:text-[13px] leading-snug">
                        <BadgeIcon ok={false} small />
                        <span className="break-words">{trad}</span>
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-1.5 sm:py-3 text-left align-top">
                      <span className="inline-flex items-start gap-1.5 sm:gap-2 text-[#0f1b2d] text-[11px] sm:text-[13px] font-medium leading-snug">
                        <BadgeIcon ok={true} small />
                        <span className="break-words">{sf}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 6. Trustpilot placeholder */}
      <section className="py-14 bg-[#f8fafc]" data-testid="trustpilot-placeholder">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center rounded-xl border border-[#e2e8f0] bg-white py-10">
          <div className="flex items-center justify-center gap-1 text-[#eab308]">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-6 h-6 fill-current" />)}
          </div>
          <div className="mt-3 text-2xl font-display font-bold text-[#0f1b2d]">Excellent · 4.9/5</div>
          <p className="mt-2 text-sm text-[#475569]">Trusted by thousands of Schengen visa applicants</p>
          <p className="mt-3 text-xs text-[#94a3b8] italic">Verified reviews coming soon</p>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="py-16 border-t border-[#e2e8f0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563eb]">Pricing FAQ</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0f1b2d]">Frequently asked questions</h2>
          </div>
          <div className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {PRICING_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`pricing-faq-${i}`} data-testid={`pricing-faq-${i}`}>
                  <AccordionTrigger className="text-left text-[#0f1b2d] font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#475569] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA banner */}
      <section className="py-16 bg-[#0f1b2d]" data-testid="pricing-bottom-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col items-center text-center gap-3">
          <h3 className="font-display font-bold text-3xl sm:text-4xl text-white">Your appointment is coming up.</h3>
          <p className="text-[#94a3b8]">Get your complete Schengen visa file in 24 hours.</p>
          <Link
            to="/apply"
            data-testid="pricing-bottom-cta-btn"
            className="mt-2 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition-all"
          >
            Start your application <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-1 text-xs text-[#94a3b8]">Takes less than 10 minutes to complete</p>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}

function BadgeIcon({ ok, small = false }) {
  const sizeWrap = small ? "w-3.5 h-3.5 sm:w-5 sm:h-5" : "w-4 h-4 sm:w-5 sm:h-5";
  const sizeIcon = small ? "w-2 h-2 sm:w-3 sm:h-3" : "w-2.5 h-2.5 sm:w-3 sm:h-3";
  if (ok) {
    return (
      <span data-testid="cell-yes" className={`inline-flex items-center justify-center ${sizeWrap} rounded-full bg-[#16a34a] flex-shrink-0`}>
        <Check className={`${sizeIcon} text-white`} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span data-testid="cell-no" className={`inline-flex items-center justify-center ${sizeWrap} rounded-full bg-[#cbd5e1] flex-shrink-0`}>
      <X className={`${sizeIcon} text-white`} strokeWidth={3} />
    </span>
  );
}

function CompareCell({ row, pkg, highlight = false }) {
  const value = row[pkg];
  const baseClass = `text-center px-1 sm:px-3 py-2 sm:py-3 ${highlight ? "bg-[#eff6ff]" : ""}`;
  if (row.type === "bool") {
    return (
      <td className={baseClass}>
        <span className="inline-flex"><BadgeIcon ok={!!value} /></span>
      </td>
    );
  }
  if (row.type === "money") {
    return <td className={`${baseClass} text-[#2563eb] font-bold text-[12px] sm:text-[13px]`}>{value}</td>;
  }
  if (row.type === "price") {
    return <td className={`${baseClass} text-[#0f1b2d] font-bold text-[12px] sm:text-[13px]`}>{value}</td>;
  }
  return <td className={`${baseClass} text-[#0f1b2d] text-[12px] sm:text-[13px]`}>{value}</td>;
}
